import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import {
  Card,
  Stack,
  Button,
  Container,
  Typography,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import Box from '@mui/material/Box';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'universal-cookie';
import { toast } from 'react-toastify';

import Iconify from '../../components/iconify';
import { API_URL } from '../../api';
import HowItWorksCard from '../../components/HowItWorksCard';
import { DataGrid, LoadPanel } from 'devextreme-react';
import {
  Column,
  FilterBuilderPopup,
  FilterPanel,
  FilterRow,
  GroupPanel,
  HeaderFilter,
  Pager,
  Paging,
  Scrolling,
  SearchPanel,
  Button as DevBtn,
} from 'devextreme-react/data-grid';

const cookies = new Cookies();

export default function ProductPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/products?status=`, {
        headers: {
          Authorization: cookies.get('TOKEN'),
        },
      });
      setProducts(response.data.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
      setLoading(false);
    }
  };

  const handleDeleteModal = (id) => {
    setDeleteId(id);
    setDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteId(null);
    setDeleteModal(false);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/products/${deleteId}`, {
        headers: {
          Authorization: cookies.get('TOKEN'),
        },
      });
      toast.success('Product deleted successfully');
      fetchProducts();
      handleCloseDeleteModal();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    }
  };

  const formatPrice = (price) => {
    return `£${(price / 100).toFixed(2)}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'draft':
        return 'warning';
      case 'inactive':
        return 'error';
      default:
        return 'default';
    }
  };

  const statusCellRender = (data) => {
    return (
      <Chip
        label={data.value}
        color={getStatusColor(data.value)}
        size="small"
        sx={{ textTransform: 'capitalize' }}
      />
    );
  };

  const priceCellRender = (data) => {
    return formatPrice(data.value);
  };

  const imageCellRender = (data) => {
    const imageUrl = data.data?.images?.[0]?.url;
    return imageUrl ? (
      <img
        src={imageUrl}
        alt={data.data.title}
        style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }}
      />
    ) : (
      <div
        style={{
          width: 50,
          height: 50,
          backgroundColor: '#f0f0f0',
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Iconify icon="mdi:image-off" sx={{ color: '#999' }} />
      </div>
    );
  };

  const titleCellRender = (data) => {
    const title = data.value || '';
    const truncated = title.length > 30 ? title.substring(0, 30) + '...' : title;
    return (
      <Tooltip title={title} placement="top-start" arrow>
        <span style={{ cursor: 'default' }}>{truncated}</span>
      </Tooltip>
    );
  };

  return (
    <>
      <Helmet>
        <title>Products | English Teacher Admin</title>
      </Helmet>

      <Container>
        <Stack className="stacky">
          <Typography variant="h4" gutterBottom style={{ fontFamily: 'Inter', fontWeight: '600' }}>
            Shop Products
          </Typography>
        <div>
            <Button
              component={Link}
              to="/products/new"
              variant="contained"
              style={{ padding: '8px 22px', fontFamily: 'Inter', fontWeight: '500', color: 'white' }}
              startIcon={<Iconify icon="eva:plus-fill" />}
            >
              Add New Product
            </Button>
          </div>
        </Stack>
        <Box sx={{ mb: 3 }}>
          <HowItWorksCard>
                        <Typography variant="caption" component="div" color="text.secondary">
                            <strong>1.</strong> Products are the digital resources on sale in the Shop area of the public site (PDFs, worksheets, past-paper packs).<br />
                            <strong>2.</strong> Click <strong>New product</strong> to add one — upload the file to Cloudinary, set title, description, subject/level tags, and price.<br />
                            <strong>3.</strong> Edit or archive products with the row buttons. Archived products disappear from the Shop but existing orders keep working.<br />
                            <strong>4.</strong> Orders (Stripe payments) land in the <em>Orders</em> tab, not here.<br />
                        </Typography>
                    </HowItWorksCard>
        </Box>


        <Card sx={{ borderRadius: '12px' }}>
          <DataGrid
            dataSource={products}
            showBorders={true}
            autoExpandAll={true}
            columnAutoWidth={true}
            keyExpr="_id"
            width="100%"
          >
            <LoadPanel enabled={loading} />
            <SearchPanel visible={true} highlightCaseSensitive={true} placeholder="Search products..." />
            <FilterRow visible={true} />
            <HeaderFilter visible={true} />
            <GroupPanel visible={true} />
            <FilterPanel visible={true} />
            <FilterBuilderPopup position={{ of: window, at: 'top', my: 'top', offset: { y: 10 } }} />
            <Scrolling mode="standard" />
            <Paging defaultPageSize={10} />
            <Pager
              visible={true}
              allowedPageSizes={[10, 25, 50, 100]}
              showPageSizeSelector={true}
              displayMode="full"
              showInfo={true}
              showNavigationButtons={true}
            />

            <Column
              caption="Image"
              width={80}
              cellRender={imageCellRender}
              allowFiltering={false}
              allowSorting={false}
            />
            <Column dataField="title" caption="Title" cellRender={titleCellRender} minWidth={150} />
            <Column dataField="subject" caption="Subject" width={140} />
            <Column dataField="level" caption="Age Group" width={120} />
            <Column dataField="price" caption="Price" cellRender={priceCellRender} alignment="left" width={70} />
            <Column dataField="status" caption="Status" cellRender={statusCellRender} width={100} />
            <Column dataField="downloadCount" caption="Downloads" width={115} alignment="left" />

            <Column type="buttons" caption="Actions" width={140}>
              <DevBtn onClick={(e) => navigate(`/products/edit/${e.row?.data?._id}`)}>
                <Button className="btn">
                  <Tooltip title="Edit" placement="top-start" arrow>
                    <Iconify icon="mdi:pencil" />
                  </Tooltip>
                </Button>
              </DevBtn>
              <DevBtn onClick={(e) => handleDeleteModal(e?.row?.data?._id)}>
                <Button className="btn">
                  <Tooltip title="Delete" placement="top-start" arrow>
                    <Iconify icon="mdi:trash-outline" />
                  </Tooltip>
                </Button>
              </DevBtn>
            </Column>
          </DataGrid>
        </Card>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteModal} onClose={handleCloseDeleteModal}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this product? This action cannot be undone.
              All associated files will also be deleted.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteModal}>Cancel</Button>
            <Button onClick={handleDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
}